// inspired by https://github.com/uidotdev/usehooks/blob/main/index.js#L45

import { StorageAdapter } from "../hooks/useKvStorage";

export class BrowserStorageAdapter extends StorageAdapter {
  /**
   * Storage Adapter for tue useKvStorage hook.
   * It wraps storages adhering to the 'Storage' interface and makes sure to also sync the changes to all subscribers.
   * It also handles serialization and deserialization JS Objects to and from json strings.
   *
   * @param getStorage List of storages to use as a backend. One storage is required.
   * The first storage is considered the main backend and will be the only one that is used for subscribing to changes.
   * All following ones will be used for pure storage only.
   *
   * When e.g. using [window.sessionStorage, window.localStorage] it means different tabs can have different values,
   * as the browser does not sync sessionStorage inbetween multiple tabs, which it would do for localStorage as main one.
   * While localStorage as a secondary backend enables more permanent storage which sessionStorage alone does not do.
   */
  constructor(protected getStorage: () => [Storage, ...Storage[]]) {
    super();
  }

  protected dispatchStorageEvent(key: string, newValue: string | null) {
    // Manually dispatching this value, makes same browser context get updates as well
    // otherwise only other tabs get it.
    this.getStorage().forEach(storageArea => {
      window.dispatchEvent(this.buildStorageEvent(key, newValue, storageArea));
    });
  }

  private buildStorageEvent(key: string, newValue: string | null, storageArea: Storage) {
    try {
      return new StorageEvent("storage", { key, newValue, storageArea });
    } catch {
      // Some environments might wrap window.sessionStorage/localStorage (e.g. in a Proxy), which can make
      // storageArea fail the StorageEvent constructor's `Storage` brand check and would otherwise throw.
      // Build the event without it and attach storageArea manually so subscribers still match by identity.
      const event = new StorageEvent("storage", { key, newValue });
      Object.defineProperty(event, "storageArea", { value: storageArea, configurable: true });
      return event;
    }
  }

  subscribe(key: string) {
    return (callback: () => void) => {
      const abortController = new AbortController();
      window.addEventListener(
        "storage",
        event => {
          // It is explicitely intended to only use the first storage for subscribing to changes.
          // See explanation of the class for the reasoning.
          if (event.storageArea === this.getStorage()[0] && event.key === key) {
            callback();
          }
        },
        { signal: abortController.signal },
      );
      return () => abortController.abort();
    };
  }

  getSnapshot(key: string) {
    for (const storage of this.getStorage()) {
      const item = storage.getItem(key);
      if (item) return item;
    }
    return undefined;
  }

  getServerSnapshot() {
    return undefined;
  }

  removeItem(key: string) {
    this.getStorage().forEach(storage => {
      storage.removeItem(key);
    });
    this.dispatchStorageEvent(key, null);
  }

  setItem(key: string, stringValue: string) {
    this.getStorage().forEach(storage => {
      storage.setItem(key, stringValue);
    });
    this.dispatchStorageEvent(key, stringValue);
  }
}
